import React, { useState, useEffect } from 'react';

interface VideoWatermarkProps {
  phoneNumber: string;
  isVisible?: boolean;
  opacity?: number;
  speed?: number;
}

const VideoWatermark: React.FC<VideoWatermarkProps> = ({ 
  phoneNumber, 
  isVisible = true,
  opacity = 0.3,
  speed = 300 // Much slower - 300 seconds (5 minutes) for one complete cycle
}) => {
  const [position, setPosition] = useState({ x: 85, y: 20 });

  useEffect(() => {
    if (!isVisible) return;

    let animationFrame: number;
    const startTime = Date.now();
    
    // More limited range on the right side - stay within 80-92% from left edge (right side)
    const rightSideArea = 80;
    const startX = rightSideArea + (Math.random() * (92 - rightSideArea));
    const startY = 10 + (Math.random() * (25 - 10));

    const animate = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      
      // Extremely slow movement - pixels per second
      const pixelsPerSecond = 4; // Even slower fixed speed
      const totalDistance = elapsed * pixelsPerSecond;
      
      // More limited right side area dimensions
      const rightSideWidth = 12; // 80% to 92% = 12% of screen width
      const verticalHeight = 15; // 10% to 25% = 15% of screen height
      
      const cycleTime = (rightSideWidth + verticalHeight) * 2 / pixelsPerSecond;
      const cycleProgress = (elapsed % cycleTime) / cycleTime;
      
      let x, y;
      
      if (cycleProgress < 0.25) {
        const edgeProgress = cycleProgress * 4;
        x = 80 + (edgeProgress * rightSideWidth);
        y = 10;
      } else if (cycleProgress < 0.5) {
        const edgeProgress = (cycleProgress - 0.25) * 4;
        x = 92;
        y = 10 + (edgeProgress * verticalHeight);
      } else if (cycleProgress < 0.75) {
        const edgeProgress = (cycleProgress - 0.5) * 4;
        x = 92 - (edgeProgress * rightSideWidth);
        y = 25;
      } else {
        const edgeProgress = (cycleProgress - 0.75) * 4;
        x = 80;
        y = 25 - (edgeProgress * verticalHeight);
      }
      
      const variationX = Math.sin(elapsed * 0.05) * 0.5; // Much slower and smaller variation
      const variationY = Math.cos(elapsed * 0.04) * 0.5; // Much slower and smaller variation
      
      const boundedX = Math.max(80, Math.min(92, x + variationX));
      const boundedY = Math.max(10, Math.min(25, y + variationY));
      
      setPosition({ x: boundedX, y: boundedY });
      
      animationFrame = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isVisible, speed]);

  if (!isVisible || !phoneNumber) {
    return null;
  }

  return (
    <div 
      className="absolute z-[100] pointer-events-none"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, -50%)',
        transition: 'all 8s cubic-bezier(0.4, 0, 0.2, 1)' // Much slower transition
      }}
    >
      <div 
        className="bg-black/30 backdrop-blur-sm text-white text-xs px-2 py-1 rounded font-mono select-none border border-white/20 shadow-sm"
        style={{ 
          opacity: Math.min(0.8, opacity),
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
          animationDuration: '10s', // Much slower pulse
          letterSpacing: '0.3px',
          fontWeight: 400
        }}
      >
        {phoneNumber}
      </div>
    </div>
  );
};

export default VideoWatermark; 